import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import AdDiv from 'helpers/ads/AdDiv';
import s from './CarousalAd.scss';

const AUTO_ADVANCE_INTERVAL = 10000; // 10 seconds

const CarousalAd = ({ parentProps = {}, adConfig = {} }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animation, setAnimation] = useState('');
  const interval = adConfig.interval || AUTO_ADVANCE_INTERVAL;

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const adsArray = useMemo(
    () => (Array.isArray(adConfig.carouselSlots) ? adConfig.carouselSlots : []),
    [adConfig.carouselSlots],
  );

  const totalItems = useMemo(() => 1 + adsArray.length, [adsArray.length]);

  const updateIndex = useCallback(
    delta => {
      setAnimation(delta > 0 ? 'frwd' : 'bkwd');
      setCurrentIndex(prevIndex => {
        if (totalItems <= 1) return 0;
        const nextIndex = prevIndex + delta;
        // Handle negative modulo correctly
        return (nextIndex % totalItems + totalItems) % totalItems;
      });
    },
    [totalItems],
  );

  // Pause auto-advance on user interaction
  const pauseAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resumeAutoAdvance = useCallback(
    () => {
      if (totalItems <= 1) return;
      pauseAutoAdvance();
      intervalRef.current = setInterval(() => {
        updateIndex(1);
      }, interval);
    },
    [totalItems, updateIndex, pauseAutoAdvance],
  );

  // Auto-advance carousel
  useEffect(
    () => {
      if (totalItems <= 1) {
        return undefined;
      }

      intervalRef.current = setInterval(() => {
        updateIndex(1);
      }, interval);

      return () => {
        pauseAutoAdvance();
      };
    },
    [totalItems, updateIndex, pauseAutoAdvance],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      pauseAutoAdvance();
    },
    [pauseAutoAdvance],
  );

  const handleNext = useCallback(
    () => {
      updateIndex(1);
      pauseAutoAdvance();
      // Resume after a delay
      timeoutRef.current = setTimeout(resumeAutoAdvance, interval * 2);
    },
    [updateIndex, pauseAutoAdvance, resumeAutoAdvance],
  );

  const handlePrev = useCallback(
    () => {
      updateIndex(-1);
      pauseAutoAdvance();
      // Resume after a delay
      timeoutRef.current = setTimeout(resumeAutoAdvance, interval * 2);
    },
    [updateIndex, pauseAutoAdvance, resumeAutoAdvance],
  );

  // Early return if no ads
  if (totalItems === 0 || !adConfig.divId) {
    return null;
  }

  const renderAdItem = useCallback(
    (ad, index, config) => {
      const isActive = currentIndex === index;

      return (
        <div
          className={`${s.adContent} ${
            isActive ? `${s.active} ${s[animation]}` : ''
          }`}
        >
          <AdDiv
            {...parentProps}
            data={{
              ...config,
              divId: `${config.divId}-${index}`,
              name: `${index > 0 ? `${config.name}-${index}` : config.name}`,
              ...ad,
            }}
            key={`${config.divId}-${index}`}
            className=""
          />
        </div>
      );
    },
    [currentIndex, parentProps],
  );

  return (
    <div className={parentProps.className}>
      <div className={s.parentWrapper}>
        <div className={s.carouselContainer}>
          {renderAdItem({}, 0, adConfig)}
          {adsArray.map((ad, index) => renderAdItem(ad, index + 1, adConfig))}
        </div>
        {totalItems > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className={classNames(s.navBtn, s.prev)}
              aria-label="Previous slide"
              aria-controls={`carousel-${adConfig.divId}`}
            />
            <button
              type="button"
              onClick={handleNext}
              className={classNames(s.navBtn, s.next)}
              aria-label="Next slide"
              aria-controls={`carousel-${adConfig.divId}`}
            />
          </>
        )}
      </div>
    </div>
  );
};

CarousalAd.propTypes = {
  adConfig: PropTypes.shape({
    divId: PropTypes.string.isRequired,
    Size: PropTypes.arrayOf(PropTypes.number),
    carouselSlots: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  parentProps: PropTypes.shape({}),
};

CarousalAd.defaultProps = {
  adConfig: {},
  parentProps: {},
};

export default CarousalAd;
